import type { PackId } from '../packs/ids'
import { glickoExpected, type GlickoRating } from './glicko2'

export const LEAGUE_IDS = [
  'duelist',
  'challenger',
  'champion',
  'king',
] as const

export type LeagueId = (typeof LEAGUE_IDS)[number]

/** I is 1, II is 2. King has no division. */
export type LeagueDivision = 1 | 2

export const PLACEMENT_GAMES = 5
export const LP_PER_DIVISION = 100
export const BASE_WIN_LP = 20
export const BASE_LOSS_LP = 15
export const DUELIST_LOSS_LP = 5
export const MIN_WIN_LP = 4
export const MAX_WIN_LP = 36
/**
 * Flat exchange when both players already sit in the same league.
 * Champion I against Champion II is this case. Duelist keeps the
 * softer loss. A Duelist win in the same division still keeps the
 * one-step floor.
 */
export const SAME_LEAGUE_LP = 20
/**
 * Fraction of a loss kept when the opponent is this many ladder steps above.
 * Index 0 is no gap. Index 6 is Duelist II against King.
 */
const RANK_GAP_LP_KEPT = [1, 0.8, 0.6, 0.45, 0.3, 0.2, 0.1] as const
/**
 * Fraction of a win kept when the opponent is this many ladder steps below.
 * A floor in WIN_GAP_FLOOR stops Glicko from crushing that win down to 1.
 * Index 0 is no gap. Index 6 is King against Duelist II.
 */
const WIN_GAP_LP_KEPT = [1, 0.85, 0.75, 0.65, 0.6, 0.55, 0.5] as const
/** Minimum LP for a win against someone this many steps below. Index 0 is unused. */
const WIN_GAP_FLOOR = [0, 12, 12, 10, 10, 10, 10] as const
/**
 * Maximum LP lost when the opponent is this many steps below.
 * Index 0 is unused. One or two steps cap at 18. Three or more cap at 16.
 */
const LOSS_GAP_CAP = [0, 18, 18, 16, 16, 16, 16] as const

/** Shop packs granted once when an account enters a higher league. */
export const LEAGUE_PROMO_PACKS = 2

/** Shop packs granted once when an account climbs a division inside a league. */
export const DIVISION_PROMO_PACKS = 1

/** Shop packs paid once when a season closes, by final league. Placement pays 0. */
export const SEASON_END_PACKS: Record<LeagueId, number> = {
  duelist: 2,
  challenger: 5,
  champion: 7,
  king: 10,
}
export const SMALL_POOL_ACCOUNTS = 20
export const QUEUE_WINDOW_START = 150
export const QUEUE_WINDOW_MID = 400
/** ±150 until this wait, then ±400. */
export const QUEUE_WAIT_MID_MS = 15_000
/** ±400 until this wait, then anyone in the queue. */
export const QUEUE_WAIT_OPEN_MS = 30_000

export const LEAGUE_BADGE_SRC: Record<LeagueId, string> = {
  duelist: '/assets/ranks/duelist.webp',
  challenger: '/assets/ranks/challenger.webp',
  champion: '/assets/ranks/champion.webp',
  king: '/assets/ranks/king.webp',
}

export interface RankedStanding {
  league: LeagueId | null
  division: LeagueDivision | null
  lp: number
  placementRemaining: number
}

export interface RankedDelta extends RankedStanding {
  lpDelta: number
  /** Standing before this duel. Absent on payloads from older servers. */
  before?: RankedStanding
  /** Shop packs granted for entering a higher league this duel. */
  promoPacks?: number
  /** Unopened pack counts after that grant. */
  packs?: Record<PackId, number>
  /** Set when this duel raised the account's highest league ever. */
  cosmeticsUnlocked?: LeagueId
}

interface LadderStep {
  league: LeagueId
  division: LeagueDivision | null
}

export const LEAGUE_STEPS: readonly LadderStep[] = [
  { league: 'duelist', division: 2 },
  { league: 'duelist', division: 1 },
  { league: 'challenger', division: 2 },
  { league: 'challenger', division: 1 },
  { league: 'champion', division: 2 },
  { league: 'champion', division: 1 },
  { league: 'king', division: null },
]

const FLOOR_STEP: Record<LeagueId, number> = {
  duelist: 0,
  challenger: 2,
  champion: 4,
  king: 6,
}

/**
 * Public badge URL for a finished-placement league.
 */
export function leagueBadgeSrc(league: LeagueId | null): string | null {
  return league ? LEAGUE_BADGE_SRC[league] : null
}

/**
 * True when this standing is still in the 5 placement duels.
 */
export function isPlacement(standing: RankedStanding): boolean {
  return standing.placementRemaining > 0 || standing.league === null
}

function stepIndex(
  league: LeagueId,
  division: LeagueDivision | null,
): number {
  const i = LEAGUE_STEPS.findIndex(
    (s) => s.league === league && s.division === division,
  )
  return Math.max(0, i)
}

/**
 * True when this duel moved the account into a higher league.
 * A division step inside the same league, placement, and demotion do not.
 */
export function leaguePromoted(
  before: RankedStanding | undefined,
  after: RankedStanding,
): boolean {
  if (!before?.league || !after.league) return false
  if (before.placementRemaining > 0) return false
  return LEAGUE_IDS.indexOf(after.league) > LEAGUE_IDS.indexOf(before.league)
}

/**
 * True when this duel climbed a division without leaving the league.
 * A league promotion, placement, and a demotion do not.
 */
export function divisionPromoted(
  before: RankedStanding | undefined,
  after: RankedStanding,
): boolean {
  if (!before?.league || !after.league) return false
  if (before.placementRemaining > 0) return false
  if (before.league !== after.league) return false
  return (
    stepIndex(after.league, after.division) >
    stepIndex(before.league, before.division)
  )
}

/**
 * Sort key for the season table (higher is better). Placement is last.
 */
export function standingRankValue(standing: RankedStanding): number {
  if (isPlacement(standing) || !standing.league) return -1
  return stepIndex(standing.league, standing.division) * 10_000 + standing.lp
}

/**
 * League after 5 placement games. Never King of Games.
 */
export function leagueFromPlacementRating(rating: number): RankedStanding {
  let step = 5
  if (rating < 1450) step = 0
  else if (rating < 1550) step = 1
  else if (rating < 1650) step = 2
  else if (rating < 1750) step = 3
  else if (rating < 1850) step = 4
  const row = LEAGUE_STEPS[step]!
  return {
    league: row.league,
    division: row.division,
    lp: 0,
    placementRemaining: 0,
  }
}

/**
 * How many ladder steps `opp` sits above `self`. Placement has no step.
 * Negative when `self` is the higher rank.
 */
function stepsAbove(
  self: RankedStanding | undefined,
  opp: RankedStanding | undefined,
): number {
  if (!self || !opp) return 0
  if (isPlacement(self) || isPlacement(opp)) return 0
  if (!self.league || !opp.league) return 0
  return (
    stepIndex(opp.league, opp.division) - stepIndex(self.league, self.division)
  )
}

function keptForRankGap(steps: number): number {
  if (steps <= 0) return 1
  const idx = Math.min(steps, RANK_GAP_LP_KEPT.length - 1)
  return RANK_GAP_LP_KEPT[idx] ?? 1
}

/**
 * Shrink a loss when the opponent's league step is higher.
 * A loss of 0 stays 0. Any real loss still costs at least 1 LP.
 */
function lossAfterHigherRank(loss: number, above: number): number {
  if (loss >= 0 || above <= 0) return loss
  return -Math.max(1, Math.round(Math.abs(loss) * keptForRankGap(above)))
}

function keptForWinGap(steps: number): number {
  if (steps <= 0) return 1
  const idx = Math.min(steps, WIN_GAP_LP_KEPT.length - 1)
  return WIN_GAP_LP_KEPT[idx] ?? 1
}

function floorForWinGap(steps: number): number {
  if (steps <= 0) return 0
  const idx = Math.min(steps, WIN_GAP_FLOOR.length - 1)
  return WIN_GAP_FLOOR[idx] ?? 0
}

function capForLossGap(steps: number): number {
  if (steps <= 0) return 0
  const idx = Math.min(steps, LOSS_GAP_CAP.length - 1)
  return LOSS_GAP_CAP[idx] ?? 16
}

/**
 * Shrink a win when the opponent's league step is lower.
 * The shrink cannot drop the win under the floor for that gap.
 */
function winAfterLowerRank(win: number, below: number): number {
  if (win <= 0 || below <= 0) return win
  const shrunk = Math.max(1, Math.round(win * keptForWinGap(below)))
  return Math.max(floorForWinGap(below), shrunk)
}

/**
 * Cap a loss when the opponent's league step is lower.
 * A loss of 0 stays 0. Same-rank losses are not capped.
 */
function lossAfterLowerRank(loss: number, below: number): number {
  if (loss >= 0 || below <= 0) return loss
  return -Math.min(Math.abs(loss), capForLossGap(below))
}

/**
 * True when both accounts have a league and it is the same one.
 * Division I against II inside that league still counts.
 */
function sameSettledLeague(
  self: RankedStanding | undefined,
  opp: RankedStanding | undefined,
): boolean {
  if (!self?.league || !opp?.league) return false
  if (isPlacement(self) || isPlacement(opp)) return false
  return self.league === opp.league
}

/**
 * True when both accounts already sit in the same division of one league.
 */
function sameSettledDivision(
  self: RankedStanding | undefined,
  opp: RankedStanding | undefined,
): boolean {
  if (!self || !opp || !sameSettledLeague(self, opp)) return false
  return self.division === opp.division
}

/**
 * LP for this seat after a ranked result.
 * The same league, including its two divisions, pays a flat even
 * amount. Duelist stays on the softer loss, and a Duelist win against
 * the same division never pays less than a win one step below.
 * A different league still scales with Glicko: a loss shrinks against
 * a higher rank, a win against a lower rank keeps a floor, and a loss
 * against a lower rank is capped.
 */
export function lpDeltaForResult(
  self: GlickoRating,
  opp: GlickoRating,
  won: boolean,
  league: LeagueId | null,
  selfStanding?: RankedStanding,
  oppStanding?: RankedStanding,
): number {
  if (!league) return 0
  // Duelist is not this flat exchange. The loss stays soft. A
  // same-division win still cannot fall under the one-step floor.
  if (league !== 'duelist' && sameSettledLeague(selfStanding, oppStanding)) {
    return won ? SAME_LEAGUE_LP : -SAME_LEAGUE_LP
  }
  const expected = glickoExpected(self, opp)
  const gap = stepsAbove(selfStanding, oppStanding)
  if (won) {
    const raw = Math.round(BASE_WIN_LP * 2 * (1 - expected))
    const win = clampInt(raw, MIN_WIN_LP, MAX_WIN_LP)
    const paid = winAfterLowerRank(win, -gap)
    if (
      league === 'duelist' &&
      sameSettledDivision(selfStanding, oppStanding)
    ) {
      return Math.max(paid, floorForWinGap(1))
    }
    return paid
  }
  if (league === 'duelist') return lossAfterHigherRank(-DUELIST_LOSS_LP, gap)
  const raw = Math.round(BASE_LOSS_LP * 2 * expected)
  const loss = lossAfterHigherRank(-clampInt(raw, 0, 40), gap)
  return lossAfterLowerRank(loss, -gap)
}

/**
 * Apply LP with intra-league demotion and league floors.
 */
export function applyLpDelta(
  standing: RankedStanding,
  delta: number,
): RankedStanding {
  if (!standing.league || standing.placementRemaining > 0) {
    return { ...standing, lp: 0 }
  }
  let step = stepIndex(standing.league, standing.division)
  let lp = standing.lp + delta
  const floor = FLOOR_STEP[standing.league]

  if (step === 6) {
    lp = Math.max(0, lp)
    const row = LEAGUE_STEPS[6]!
    return {
      league: row.league,
      division: row.division,
      lp,
      placementRemaining: 0,
    }
  }

  while (step < 6 && lp >= LP_PER_DIVISION) {
    lp -= LP_PER_DIVISION
    step += 1
    if (step === 6) break
  }
  while (lp < 0) {
    if (step <= floor) {
      lp = 0
      break
    }
    step -= 1
    lp += LP_PER_DIVISION
  }

  const row = LEAGUE_STEPS[step]!
  return {
    league: row.league,
    division: row.division,
    lp,
    placementRemaining: 0,
  }
}

/**
 * Resolve a league + division pair from DevTools input.
 */
export function parseLeagueStep(
  league: unknown,
  division: unknown,
): (typeof LEAGUE_STEPS)[number] | null {
  let div: 1 | 2 | null = null
  if (division === 1 || division === 2) div = division
  else if (division === '1' || division === '2') div = Number(division) as 1 | 2
  return (
    LEAGUE_STEPS.find(
      (step) => step.league === league && step.division === div,
    ) ?? null
  )
}

/**
 * Matchmaking rating window in displayed Glicko points.
 */
export function queueRatingWindow(
  waitMs: number,
  smallPool: boolean,
): number {
  if (smallPool || waitMs >= QUEUE_WAIT_OPEN_MS) return Number.POSITIVE_INFINITY
  if (waitMs >= QUEUE_WAIT_MID_MS) return QUEUE_WINDOW_MID
  return QUEUE_WINDOW_START
}

function clampInt(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, Math.round(n)))
}
